import { createContext, useContext, type MutableRefObject } from "react";
import type { ArchitectureViewHandle } from "../architecture-view/ArchitectureView.js";
import type { HoverStore } from "../board/hoverStore.js";

/**
 * The composition's shared board transport: the imperative board handle
 * (fly/getViewport/restoreViewport) and the board ↔ nav hover channel. Both are
 * stable for the composition's lifetime — the board registers itself on
 * `boardRef`; nav rows and board nodes read/write the one `hoverStore`.
 *
 * Owned by a provider (not the controller) so primitives can self-source the
 * handle instead of receiving it prop-drilled from the composition.
 */
export interface BoardHandle {
  readonly boardRef: MutableRefObject<ArchitectureViewHandle | null>;
  readonly hoverStore: HoverStore;
}

export const BoardHandleContext = createContext<BoardHandle | null>(null);

/** The composition's board handle. Throws outside the provider. */
export function useBoardHandle(): BoardHandle {
  const v = useContext(BoardHandleContext);
  if (!v) {
    throw new Error("useBoardHandle must be used within a <BoardHandleProvider>");
  }
  return v;
}

/**
 * Like {@link useBoardHandle} but returns null when no provider is mounted — for
 * primitives that OPTIONALLY use the board handle and must also work in
 * provider-less compositions (the standalone board / marketing demo).
 */
export function useBoardHandleOptional(): BoardHandle | null {
  return useContext(BoardHandleContext);
}
